import dayjs from 'dayjs';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';

import axios from 'services/axios';

import AppLogger from 'utils/AppLogger';

import {
	ExportFilterTypes,
	FilterType,
	convertFilterValueToDTO,
	convertSortValueToDTO
} from './DynamicListFilterUtils';
import { DynamicListComponentMap } from './DynamicListTable';
import { setDynamicListExporting } from './dynamicListSlice';

const useListExport = () => {
	const { t } = useTranslation();
	const { showError, showInfo } = useAlert();
	const dispatch = useDispatch();
	const { DateFormat, TimeZone } = useLocale();

	const handleListExport = useCallback(
		async ({ id, fetchUrl, columns, exportName, defaultSort, defaultFilters, exportNameTranslationObject }) => {
			dispatch(setDynamicListExporting(true));

			const storedColumns = window.localStorage.getItem(`${id}_columns`);
			const storedColumnsJson = storedColumns ? JSON.parse(storedColumns) : null;
			const tempColumns = structuredClone((storedColumnsJson ?? columns).filter((col) => col?.show !== false));
			tempColumns.forEach((col, i) => {
				const { renderCell } = col;
				if (renderCell) {
					const exportFields = DynamicListComponentMap[renderCell].exportFields;
					if (exportFields) {
						exportFields.forEach((field) => {
							const isFound = tempColumns.some((col) => col.accessor === field);

							if (!isFound) {
								tempColumns.splice(i + 1, 0, {
									accessor: field,
									filterType: FilterType.string
								});
							}
						});
					}
				}
			});

			const requestJson = {
				filter:
					defaultFilters?.length > 0
						? defaultFilters.map((filter) => convertFilterValueToDTO(filter))
						: undefined,
				sort: defaultSort ? convertSortValueToDTO(defaultSort) : undefined,
				export: {
					headers: tempColumns.map((col) => ({
						label: col.Header ? t(col.Header) : t(col.accessor),
						key: col.accessor,
						type: ExportFilterTypes[col.filterType] ?? FilterType.string
					})),
					timezone: dayjs().tz(TimeZone).utcOffset(),
					dateFormat: DateFormat.dayjsFormat,
					name: exportName,
					translationObject: JSON.stringify(exportNameTranslationObject)
				}
			};

			let success = false;

			await axios
				.post(fetchUrl, requestJson)
				.then((response) => {
					if (response.status !== 200) {
						showError({
							message: response.data?.code
						});
					} else {
						showInfo({
							header: 'infoMsg.exportInProgressHeader',
							message: 'infoMsg.exportInProgressMessage'
						});
						success = true;
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					showError({
						message: error.response?.data?.code
					});
				});

			if (success) {
				setTimeout(() => {
					dispatch(setDynamicListExporting(false));
				}, 15000);
			}

			return success;
		},
		[DateFormat, TimeZone]
	);

	return handleListExport;
};

export default useListExport;
